import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DeviceState } from "@shared/sync/state.js";
import { sameMeter, type Meter } from "@web-shared/lib/meter";
import { type AuditionOptions,
  ArrangementPlayer,
  sourceSignature,
  type ClipSource,
  type LaneLevel,
  type LoadProgress,
  type MidiNoteEvent,
  type MidiSource,
  type PlayerStatus,
  type SceneLaunchResult,
} from "@web-shared/lib/audio-engine";
import { patchById, type SynthPatch } from "@web-shared/lib/synth";
import type { LatencyEstimate } from "@web-shared/lib/latency";
import { LiveTransport } from "@web-shared/lib/transport";
import type { Onset } from "@web-shared/lib/stretch/onsets";

export interface ArrangementPlayerApi {
  status: PlayerStatus;
  progress: LoadProgress | null;
  /** Timeline position in seconds, updated per animation frame while playing. */
  position: number;
  duration: number;
  loop: boolean;
  error: string | null;
  /** Sources that referenced audio which could not be loaded. */
  missing: number;
  toggle: () => void;
  /** Live's insert marker, in seconds: where Space plays from. */
  insertSec: number;
  /** Move the insert marker (a click in a track, a selection). Stopped, the
   * playhead moves with it; playing, the music plays on. */
  setInsert: (sec: number) => void;
  /** Space: play from the insert marker, or stop and go back to it. */
  playFromInsert: () => void;
  /** Shift+Space: continue from where playback last stopped (or stop). */
  resumeFromStop: () => void;
  /** Alt+Space: play [from, to) once, then back to the marker (or stop). */
  playRange: (fromSec: number, toSec: number) => void;
  /** Stop the arrangement; the playhead goes back to the insert marker. */
  stopToInsert: () => void;
  /** Double-click Stop / Home: the insert marker back to the start. */
  returnToStart: () => void;
  /** A click in the ruler: playing, jump there on the next quantization
   * line; stopped, play from there. Moves the insert marker too. */
  jumpTo: (sec: number) => void;
  seek: (seconds: number) => void;
  stop: () => void;
  setLoop: (loop: boolean) => void;
  /** Play one clip on its own, independent of the transport. `options.sync`
   * waits for the next bar while something plays; `options.loop` keeps a
   * loop going until the next preview or `stopAudition`. */
  auditionClip: (source: ClipSource, options?: AuditionOptions) => void;
  /** Same contract, for a MIDI clip — synthesised, nothing fetched. */
  auditionMidiClip: (source: MidiSource, options?: AuditionOptions) => void;
  /** One note through its lane, now, beside whatever auditions; returns
   * its release (key up). */
  auditionNote: (laneId: string, note: MidiNoteEvent) => () => void;
  /** Id of the clip currently auditioning, for play-state in the UI. */
  auditioningId: string | null;
  /** Id of the clip asked for but not yet sounding (its bytes or render
   * on the way) — a row's spinner. */
  auditionPendingId: string | null;
  stopAudition: () => void;
  setMuted: (laneId: string, muted: boolean) => void;
  setSoloed: (laneId: string, soloed: boolean) => void;
  /** MIDI preview timbre for a lane (playback-only). */
  setLanePatch: (laneId: string, patch: SynthPatch) => void;
  /** Hear a few notes on a patch, on no lane — an instrument picker's tile. */
  previewPatch: (patch: SynthPatch, notes: MidiNoteEvent[]) => void;
  /** Post-fader peak for a lane / the master, read per frame by the mixer. */
  meterLevel: (laneId: string) => number;
  masterLevel: () => number;
  setVolume: (v: number) => void;
  /** The Set's master fader + pan, applied before the limiter. */
  setMasterLevel: (level: { volume: number; pan: number }) => void;
  /** A lane's send levels and the Set's return tracks — heard dry. */
  setLaneSends: (laneId: string, sends: number[]) => void;
  setReturns: (returns: Array<{ volume: number; pan: number; mute: boolean; solo: boolean }>) => void;
  /** The Set's own fader/pan for a lane, applied to playback. */
  setLaneLevel: (laneId: string, level: LaneLevel) => void;
  /** The Set's device chains, heard approximately (lib/device-graph.ts):
   * a lane's, the master's, a return's — and the switch for all of them. */
  setLaneDevices: (laneId: string, devices: readonly DeviceState[] | undefined) => void;
  setMasterDevices: (devices: readonly DeviceState[] | undefined) => void;
  setReturnDevices: (index: number, devices: readonly DeviceState[] | undefined) => void;
  setHearDevices: (on: boolean) => void;
  /** Session View: launch a slot clip on its lane (replacing the lane's
   * current one), stop a lane, stop everything. Lane id → clip id of what
   * is launched. Playback-only. */
  /** Resolves to the beat the launch sounds at (null when nothing launched). */
  launchSlot: (laneId: string, clipId: string, source: ClipSource | MidiSource, loop?: boolean) => Promise<number | null>;
  /** A whole scene row, launched as one: every clip in it starts and every
   * lane it leaves empty stops, all on the SAME grid line. Not a loop over
   * `launchSlot` — that would let a row straddle a bar. */
  launchScene: (
    items: ReadonlyArray<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop?: boolean }>,
    stopLaneIds?: readonly string[],
  ) => Promise<SceneLaunchResult>;

  stopSlot: (laneId: string) => void;
  stopAllSlots: () => void;
  /** An edit reaches what is launched: swap any of these launches whose
   * clip no longer sounds like its source, on one grid line. */
  refreshSlots: (items: ReadonlyArray<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop?: boolean }>) => void;
  /** An edit reaches the preview: restart it as edited if `src` is the
   * clip auditioning and it changed. */
  refreshAudition: (src: ClipSource | MidiSource) => void;
  sessionPlaying: Record<string, string>;
  /** Lane id → clip id of a launch whose stop is queued for a coming line:
   * still sounding, on its way out. */
  sessionStopping: Record<string, string>;
  /** Lanes the Session View has taken over — a launch or a clip stop there
   * silences the lane's timeline clips until "Back to Arrangement". */
  overridden: ReadonlySet<string>;
  /** Live's "Back to Arrangement": one lane, or every lane, drops what the
   * Session View launched and plays its timeline clips again. */
  backToArrangement: (laneId?: string) => void;
  /** A launched clip's position in its loop (seconds from its start), or
   * null. Re-read every frame while a consumer follows (`setSlotFollow`). */
  slotPositionSec: (laneId: string) => number | null;
  /** The launched clip's loop length on a lane, in seconds; null when
   * nothing is launched there. */
  slotPeriodSec: (laneId: string) => number | null;
  /** Tick a re-render every frame while a slot plays — only while a view
   * (a piano roll on a launched clip) needs it, never for the page at large. */
  setSlotFollow: (on: boolean) => void;
  /** Launch timing for Session clips: next bar, next beat, now, or custom beats. */
  setLaunchQuantize: (q: "bar" | "beat" | "none" | number) => void;
  /** Render the whole arrangement to a buffer, for a mixdown download. */
  renderMixdown: (
    onProgress?: (p: LoadProgress) => void,
    laneFilter?: (laneId: string) => boolean,
    respectGates?: boolean | "mutes",
    preFader?: boolean,
  ) => Promise<AudioBuffer>;
  /** Bumps whenever another object decodes — re-read `peaks` on change. */
  decodedVersion: number;
  /** Decode dropped-file bytes with the playback context. */
  decode: (bytes: ArrayBuffer) => Promise<AudioBuffer>;
  /** An object resampled by semitones — pitch and length together. */
  renderRepitched: (url: string, semitones: number, offsetSec?: number, durationSec?: number) => Promise<AudioBuffer>;
  /** The decoded object behind a URL, fetched if the transport hasn't. */
  bufferFor: (url: string) => Promise<AudioBuffer>;
  /** An object's transients (the clip editor's ticks), computed off the
   * main thread once per object. */
  onsetsFor: (url: string) => Promise<Onset[]>;
  /** Sources rendered alone and dry, as they play — a clip bounce. */
  renderSources: (sources: readonly ClipSource[], durationSec: number) => Promise<AudioBuffer>;
  /** One clip resolved for a caller's own render: the buffer to play (the
   * object, or its stretched copy for a warped clip at another tempo) and
   * the `start()` arguments and rate; null until decoded. */
  resolvedFor: (src: ClipSource) => { buffer: AudioBuffer; offset: number; playFor: number; rate: number } | null;
  /** Loop region in seconds; null plays through. Playback-only. */
  loopRange: { startSec: number; endSec: number } | null;
  setLoopRange: (range: { startSec: number; endSec: number } | null) => void;
  metronome: boolean;
  setMetronome: (on: boolean) => void;
  /** Tempo the metronome clicks at. */
  setTempo: (bpm: number) => void;
  /** Meter the metronome counts in and launches wait for. */
  setMeter: (meter: Meter) => void;
  recording: boolean;
  /** Start the mic from the playhead (starts the transport if stopped). */
  /** `countInBars` of clicks first when the transport is stopped. */
  startRecording: (countInBars?: number) => Promise<void>;
  /** Stop and get the take. Null when nothing was captured. */
  stopRecording: () => Promise<{ buffer: AudioBuffer; startSec: number } | null>;
  /** Play clicks, record them back, measure the round trip (ms). */
  calibrateLatency: () => Promise<LatencyEstimate | null>;
  /** Double-sided waveform overview of an object — `buckets` interleaved
   * min/max pairs; null until it has decoded (or `ensurePeaks` has run). */
  peaks: (url: string, buckets: number, offsetSec?: number, durationSec?: number) => Float32Array | null;
  /** Fetch an object to draw it; `decodedVersion` bumps when it can.
   * `later` queues behind every `now`; `preview`, the object's MP3, is drawn
   * from instead of the object where there is one. */
  ensurePeaks: (url: string, priority?: "now" | "later", preview?: string) => void;
  /** Resolves when no object is downloading (`ArrangementPlayer.fetchesSettled`). */
  fetchesSettled: () => Promise<void>;
}

export { sourceSignature } from "@web-shared/lib/audio-engine";

/**
 * Drives an ArrangementPlayer from React.
 *
 * The player owns the clock; this only samples it for rendering. Position lives
 * in state updated per animation frame rather than being derived during render,
 * because the AudioContext clock advances on its own and React has no idea.
 */
/** The lane an instrument preview plays through: none of the arrangement's. */
const PREVIEW_LANE = "instrument-preview";

export function useArrangementPlayer(
  sources: ClipSource[],
  fallbackDuration = 0,
  midi: MidiSource[] = [],
  options: {
    prefetch?: boolean;
    /** Lane id → instrument id (lib/arrangement.ts `laneInstruments`): what
     * each MIDI lane plays. A lane left out plays the default. */
    instruments?: Readonly<Record<string, string>>;
  } = {},
): ArrangementPlayerApi {
  const [player, setPlayer] = useState(() => new ArrangementPlayer());

  const [status, setStatus] = useState<PlayerStatus>("idle");
  const [progress, setProgress] = useState<LoadProgress | null>(null);
  const [position, setPosition] = useState(0);
  const [loop, setLoopState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(0);
  const [auditioningId, setAuditioningId] = useState<string | null>(null);
  const [auditionPendingId, setAuditionPendingId] = useState<string | null>(null);
  const [decodedVersion, setDecodedVersion] = useState(0);
  const [loopRange, setLoopRangeState] = useState<{ startSec: number; endSec: number } | null>(null);
  const [metronome, setMetronomeState] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sessionPlaying, setSessionPlaying] = useState<Record<string, string>>({});
  const [sessionStopping, setSessionStopping] = useState<Record<string, string>>({});
  const [overridden, setOverridden] = useState<ReadonlySet<string>>(() => new Set());
  const abortRef = useRef<AbortController | null>(null);

  // Identity of the material, not of the array — callers rebuild `sources` on
  // every render, and reloading every frame would be catastrophic. MIDI is
  // keyed by note CONTENT, not count: a pitch drag changes what should sound
  // without changing how many notes there are.
  const signature = useMemo(
    () =>
      sources.map(sourceSignature).join("|") +
      "//" +
      midi
        .map(
          (s) =>
            `${s.id}:${s.startSec}:${s.durationSec}:` +
            // By what the source was made from when it says: a rebuild that
            // rolled chance again is the same material.
            (s.key ?? s.notes.map((n) => `${n.pitch}@${n.startSec.toFixed(4)}+${n.durationSec.toFixed(4)}v${n.velocity}`).join(",")),
        )
        .join("|"),
    [sources, midi],
  );
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;
  const statusRef = useRef(status);
  statusRef.current = status;

  player.fallbackDuration = fallbackDuration;
  // Handed over every render, like fallbackDuration — it is read at schedule
  // time only, and the signature effect stops playback when the material changes.
  player.setMidiSources(midi);
  // Read off the player, not the sources: stems have no declared length until
  // they decode, so this grows once loading finishes.
  const duration = Math.max(player.duration, fallbackDuration);

  // A changed arrangement. Once audio is loaded the transport SURVIVES it —
  // an edit mid-playback reschedules from where the playhead is (fetching any
  // clip the change introduced first), which is what makes editing while
  // listening possible at all. Before anything is loaded there is nothing to
  // keep: reset, and the next play fetches the new material.
  /** A change that landed while the first load ran: applied once it ends. */
  const reloadAfterLoad = useRef(false);
  useEffect(() => {
    const status = statusRef.current;
    if (status === "loading") {
      // Let the load finish — a collaborator's edit mid-load must not
      // flip the transport back to idle — and reload afterwards.
      reloadAfterLoad.current = true;
      return;
    }
    if (status === "ready" || status === "playing") {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      void player
        .replaceSources(sourcesRef.current, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setMissing(result.failed);
          setPosition(player.position);
          if (!player.isPlaying && statusRef.current === "playing") setStatus("ready");
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setError(err instanceof Error ? err.message : "Could not reload the audio.");
        });
      return;
    }
    abortRef.current?.abort();
    // Idle: nothing scheduled to keep. Launched Session clips are not the
    // arrangement's and keep looping. The playhead stays on the insert
    // marker — an edit must not look like it moved where the next play
    // starts or where ⌘E cuts.
    player.stopArrangement();
    setStatus("idle");
    setProgress(null);
    const insert = transportRef.current?.insertSec ?? 0;
    player.seek(insert);
    setPosition(insert);
    setMissing(0);
    setError(null);
  }, [signature, player]);

  // The seed's instruments. Keyed by content, so a rebuilt record is no
  // change; a real one mid-play reschedules, as a picked timbre does.
  const instrumentsKey = JSON.stringify(Object.entries(options.instruments ?? {}).sort(([a], [b]) => a.localeCompare(b)));
  useEffect(() => {
    const entries = JSON.parse(instrumentsKey) as Array<[string, string]>;
    if (entries.length === 0) return;
    for (const [laneId, id] of entries) player.setLanePatch(laneId, patchById(id));
    if (player.isPlaying) void player.reschedule();
  }, [instrumentsKey, player]);

  // A view that draws its waveforms from previews fetches the material
  // behind them once they are up (`prefetch`), so Play does not start from
  // nothing. Off by default: a seed page loads on first Play, never on mount.
  const prefetch = options.prefetch === true;
  useEffect(() => {
    if (prefetch) player.prefetch(sourcesRef.current);
  }, [signature, prefetch, player]);

  useEffect(() => {
    player.onAuditionChange = (id) => {
      setAuditioningId(id);
      setAuditionPendingId(null);
    };
    player.onDecoded = () => setDecodedVersion((v) => v + 1);
    player.onSessionChange = (playing) => {
      setSessionPlaying(playing);
      setSessionStopping(player.sessionStopping);
    };
    player.onOverrideChange = setOverridden;
    return () => {
      player.onAuditionChange = null;
      player.onDecoded = null;
      player.onSessionChange = null;
      player.onOverrideChange = null;
    };
  }, [player]);

  // A player is closed on unmount. StrictMode unmounts once and mounts
  // again with the same one; a closed player is replaced rather than reused
  // half-working, and every effect keyed on it runs again for the new one.
  useEffect(() => {
    if (player.isDisposed) {
      setPlayer(new ArrangementPlayer());
      return;
    }
    return () => {
      abortRef.current?.abort();
      player.dispose();
    };
  }, [player]);

  // Tick while a slot plays so a piano roll can follow a launched clip; the
  // value itself is read from the player at render time.
  const [, setSlotFrame] = useState(0);
  const [slotFollow, setSlotFollow] = useState(false);
  const anyLaunched = Object.keys(sessionPlaying).length > 0;
  useEffect(() => {
    if (!anyLaunched || !slotFollow) return;
    let frame = 0;
    const step = () => {
      setSlotFrame((f) => (f + 1) % 1_000_000);
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [anyLaunched, slotFollow]);

  /** The Live transport, once made (it is built further down, after `play`). */
  const transportRef = useRef<LiveTransport | null>(null);

  // Sample the audio clock while playing.
  useEffect(() => {
    if (status !== "playing") return;
    let frame = 0;
    const step = () => {
      player.tick();
      // A run that ended by itself (the selection's end, the Set's end)
      // goes back to the marker, as a stop would.
      transportRef.current?.afterTick();
      setPosition(player.position);
      if (!player.isPlaying) setStatus("ready");
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [status, player]);

  /** Load what isn't loaded yet, then play — from `from` when given, else
   * from where the transport is. */
  const startPlayback = useCallback(
    async (from?: number): Promise<void> => {
      setError(null);
      const at = from ?? player.position;
      if (player.playableCount === 0 || progress === null || player.needsLoadForPlay(sourcesRef.current, at)) {
        const controller = new AbortController();
        abortRef.current = controller;
        setStatus("loading");
        try {
          // Objects keep landing behind the Play, so this keeps reporting.
          const result = await player.loadForPlay(
            sourcesRef.current,
            at,
            (p) => {
              setProgress(p);
              setMissing(p.failed);
            },
            controller.signal,
          );
          if (controller.signal.aborted) return;
          if (player.playableCount === 0) {
            setError(
              result.total === 0
                ? "Nothing in this arrangement has audio attached."
                : "None of this arrangement's audio could be loaded.",
            );
            setStatus("idle");
            return;
          }
        } catch (err) {
          if (controller.signal.aborted) return;
          setError(err instanceof Error ? err.message : "Could not load the audio.");
          setStatus("idle");
          return;
        }
      }
      if (reloadAfterLoad.current) {
        // The arrangement changed during the load: bring in what it now
        // needs before playing (only the missing objects are fetched).
        reloadAfterLoad.current = false;
        const controller = new AbortController();
        abortRef.current = controller;
        const result = await player.replaceSources(sourcesRef.current, controller.signal);
        if (controller.signal.aborted) return;
        setMissing(result.failed);
      }
      await player.play(from);
      setStatus("playing");
    },
    [player, progress],
  );
  const play = useCallback(
    (from?: number) =>
      void startPlayback(from).catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Playback failed.");
        setStatus("idle");
      }),
    [startPlayback],
  );

  const toggle = useCallback(() => {
    transportRef.current?.release();
    if (player.isPlaying) {
      player.pause();
      setPosition(player.position);
      setStatus("ready");
      return;
    }
    play();
  }, [player, play]);

  // ---- Live's transport: the insert marker (lib/transport.ts) -------------
  const [insertSec, setInsertSecState] = useState(0);
  const playRef = useRef(play);
  playRef.current = play;
  const transport = useMemo(
    () => new LiveTransport(player, (from) => playRef.current(from), setInsertSecState),
    [player],
  );
  transportRef.current = transport;
  /** After a transport call, show where the playhead now is. */
  const settle = useCallback(() => {
    setPosition(player.position);
    if (!player.isPlaying) setStatus((s) => (s === "playing" ? "ready" : s));
  }, [player]);
  const setInsert = useCallback((sec: number) => { transport.setInsert(sec); settle(); }, [transport, settle]);
  const stopToInsert = useCallback(() => { transport.stop(); settle(); }, [transport, settle]);
  const playFromInsert = useCallback(() => { transport.playFromInsert(); settle(); }, [transport, settle]);
  const resumeFromStop = useCallback(() => { transport.resume(); settle(); }, [transport, settle]);
  const playRange = useCallback((fromSec: number, toSec: number) => { transport.playRange(fromSec, toSec); settle(); }, [transport, settle]);
  const returnToStart = useCallback(() => { transport.returnToStart(); settle(); }, [transport, settle]);
  const jumpTo = useCallback((sec: number) => { transport.jumpTo(sec); settle(); }, [transport, settle]);

  const seek = useCallback(
    (seconds: number) => {
      player.seek(seconds);
      setPosition(player.position);
    },
    [player],
  );

  const auditionClip = useCallback(
    (source: ClipSource, options?: AuditionOptions) => {
      setError(null);
      setAuditionPendingId(source.id);
      void player
        .auditionOnce(source, undefined, options)
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : "Could not play that clip.");
        })
        .finally(() => setAuditionPendingId((id) => (id === source.id ? null : id)));
    },
    [player],
  );

  const auditionMidiClip = useCallback(
    (source: MidiSource, options?: AuditionOptions) => {
      setError(null);
      void player.auditionMidiOnce(source, options).catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Could not play that clip.");
      });
    },
    [player],
  );

  const auditionNote = useCallback((laneId: string, note: MidiNoteEvent) => player.auditionNote(laneId, note), [player]);

  const stop = useCallback(() => {
    player.stop();
    setPosition(0);
    setStatus(player.playableCount > 0 ? "ready" : "idle");
  }, [player]);

  const setLoop = useCallback(
    (next: boolean) => {
      player.loop = next;
      setLoopState(next);
    },
    [player],
  );

  return {
    status,
    progress,
    position,
    duration,
    loop,
    error,
    missing,
    toggle,
    insertSec,
    setInsert,
    playFromInsert,
    resumeFromStop,
    playRange,
    stopToInsert,
    returnToStart,
    jumpTo,
    seek,
    stop,
    auditionClip,
    auditionNote,
    auditionPendingId,
    auditionMidiClip,
    auditioningId,
    stopAudition: useCallback(() => player.stopAudition(), [player]),
    setLoop,
    setMuted: useCallback((id: string, m: boolean) => player.setMuted(id, m), [player]),
    setSoloed: useCallback((id: string, s: boolean) => player.setSoloed(id, s), [player]),
    setLanePatch: useCallback(
      (id: string, patch: SynthPatch) => {
        player.setLanePatch(id, patch);
        // Tones are scheduled with the clips, so a timbre change mid-play reschedules.
        if (player.isPlaying) void player.reschedule();
      },
      [player],
    ),
    previewPatch: useCallback(
      (patch: SynthPatch, notes: MidiNoteEvent[]) => {
        player.setLanePatch(PREVIEW_LANE, patch);
        const durationSec = notes.reduce((m, n) => Math.max(m, n.startSec + n.durationSec), 0);
        void player
          .auditionMidiOnce({ id: `preview:${patch.id}`, laneId: PREVIEW_LANE, startSec: 0, durationSec, notes })
          .catch(() => {});
      },
      [player],
    ),
    meterLevel: useCallback((id: string) => player.meterLevel(id), [player]),
    masterLevel: useCallback(() => player.masterLevel(), [player]),
    setVolume: useCallback((v: number) => player.setVolume(v), [player]),
    setMasterLevel: useCallback((level: { volume: number; pan: number }) => player.setMasterLevel(level), [player]),
    setLaneSends: useCallback((laneId: string, sends: number[]) => player.setLaneSends(laneId, sends), [player]),
    setReturns: useCallback(
      (returns: Array<{ volume: number; pan: number; mute: boolean; solo: boolean }>) => player.setReturns(returns),
      [player],
    ),
    setLaneLevel: useCallback((id: string, level: LaneLevel) => player.setLaneLevel(id, level), [player]),
    setLaneDevices: useCallback((id: string, devices: readonly DeviceState[] | undefined) => player.setLaneDevices(id, devices), [player]),
    setMasterDevices: useCallback((devices: readonly DeviceState[] | undefined) => player.setMasterDevices(devices), [player]),
    setReturnDevices: useCallback((index: number, devices: readonly DeviceState[] | undefined) => player.setReturnDevices(index, devices), [player]),
    setHearDevices: useCallback((on: boolean) => player.setHearDevices(on), [player]),
    decode: useCallback((bytes: ArrayBuffer) => player.decode(bytes), [player]),
    renderRepitched: useCallback(
      (url: string, semitones: number, offsetSec?: number, durationSec?: number) =>
        player.renderRepitched(url, semitones, offsetSec, durationSec),
      [player],
    ),
    bufferFor: useCallback((url: string) => player.bufferFor(url), [player]),
    onsetsFor: useCallback((url: string) => player.onsetsFor(url), [player]),
    renderSources: useCallback((sources: readonly ClipSource[], durationSec: number) => player.renderSources(sources, durationSec), [player]),
    resolvedFor: useCallback((src: ClipSource) => player.resolvedFor(src), [player]),
    loopRange,
    setLoopRange: useCallback(
      (range: { startSec: number; endSec: number } | null) => {
        player.loopRange = range;
        setLoopRangeState(range);
        // A region set while playing takes effect now, not at the next wrap.
        if (player.isPlaying && range && (player.position < range.startSec || player.position >= range.endSec)) {
          void player.play(range.startSec);
        }
      },
      [player],
    ),
    metronome,
    setMetronome: useCallback(
      (on: boolean) => {
        player.metronome = on;
        setMetronomeState(on);
        // Clicks are scheduled with the clips, so toggling mid-play reschedules.
        if (player.isPlaying) void player.reschedule();
      },
      [player],
    ),
    setTempo: useCallback(
      (bpm: number) => {
        player.tempo = bpm;
      },
      [player],
    ),
    setMeter: useCallback(
      (meter: Meter) => {
        if (sameMeter(player.meter, meter)) return;
        player.meter = meter;
        // Clicks are scheduled with the clips, so a new count reschedules.
        if (player.metronome && player.isPlaying) void player.reschedule();
      },
      [player],
    ),
    recording,
    startRecording: useCallback(async (countInBars?: number) => {
      await player.startRecording(countInBars);
      setRecording(true);
      if (player.isPlaying) setStatus("playing");
    }, [player]),
    calibrateLatency: useCallback(() => player.calibrateLatency(), [player]),
    stopRecording: useCallback(async () => {
      try {
        return await player.stopRecording();
      } finally {
        setRecording(false);
      }
    }, [player]),
    launchSlot: useCallback(
      (laneId: string, clipId: string, source: ClipSource | MidiSource, loop?: boolean) =>
        player.launchSlot(laneId, clipId, source, loop).catch((err: unknown): number | null => {
          setError(err instanceof Error ? err.message : "Could not launch that clip.");
          return null;
        }),
      [player],
    ),
    launchScene: useCallback(
      (
        items: ReadonlyArray<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop?: boolean }>,
        stopLaneIds?: readonly string[],
      ) =>
        player
          .launchScene(items, stopLaneIds)
          .then((result) => {
            // One clip that would not load is one silent lane and no message;
            // a row where NOTHING loaded looks like a dead button, so it says
            // so rather than leaving the Set mysteriously silent.
            if (result.started === 0 && result.failed > 0) setError("Could not load that scene's audio.");
            return result;
          })
          .catch((err: unknown): SceneLaunchResult => {
            setError(err instanceof Error ? err.message : "Could not launch that scene.");
            return { started: 0, failed: items.length, atBeat: null, launched: [] };
          }),
      [player],
    ),
    stopSlot: useCallback((laneId: string) => player.stopSlot(laneId), [player]),
    stopAllSlots: useCallback(() => player.stopAllSlots(), [player]),
    refreshSlots: useCallback(
      (items: ReadonlyArray<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop?: boolean }>) =>
        void player.refreshSlots(items).catch(() => {}),
      [player],
    ),
    refreshAudition: useCallback((src: ClipSource | MidiSource) => void player.refreshAudition(src).catch(() => {}), [player]),
    sessionPlaying,
    sessionStopping,
    overridden,
    backToArrangement: useCallback((laneId?: string) => player.backToArrangement(laneId), [player]),
    slotPositionSec: useCallback((laneId: string) => player.slotPositionSec(laneId), [player]),
    slotPeriodSec: useCallback((laneId: string) => player.slotPeriodSec(laneId), [player]),
    setSlotFollow,
    setLaunchQuantize: useCallback(
      (q: "bar" | "beat" | "none" | number) => {
        player.launchQuantize = q;
      },
      [player],
    ),
    renderMixdown: useCallback(
      (onProgress?: (p: LoadProgress) => void, laneFilter?: (laneId: string) => boolean, respectGates?: boolean | "mutes", preFader?: boolean) =>
        player.renderMixdown(sourcesRef.current, onProgress, laneFilter, respectGates, preFader),
      [player],
    ),
    decodedVersion,
    peaks: useCallback(
      (url: string, buckets: number, offsetSec?: number, durationSec?: number) =>
        player.peaks(url, buckets, offsetSec, durationSec),
      [player],
    ),
    fetchesSettled: useCallback(() => player.fetchesSettled(), [player]),
    ensurePeaks: useCallback(
      (url: string, priority?: "now" | "later", preview?: string) => player.ensurePeaks(url, priority, preview),
      [player],
    ),
  };
}
